import { type Accessor, createSignal, onCleanup } from "solid-js";

/**
 * Reactive `matchMedia` accessor. Returns `fallback` in non-DOM/SSR contexts.
 * Cleans up its listener automatically with the owning reactive scope.
 */
function createMediaQuery(query: string, fallback = false): Accessor<boolean> {
  if (typeof globalThis.matchMedia !== "function") {
    return () => fallback;
  }

  const list = globalThis.matchMedia(query);
  const [matches, setMatches] = createSignal(list.matches);

  const handler = (event: MediaQueryListEvent) => {
    setMatches(event.matches);
  };
  list.addEventListener("change", handler);
  onCleanup(() => {
    list.removeEventListener("change", handler);
  });

  return matches;
}

/**
 * True when the UI should use a touch-friendly bottom sheet instead of an
 * anchored popover: small viewports or coarse (touch) pointers — covering
 * phones and touch tablets while keeping the popover on desktop/laptops.
 */
export function createUsesSheet(): Accessor<boolean> {
  return createMediaQuery("(max-width: 768px), (pointer: coarse)");
}

/**
 * True when the user has requested reduced motion. Gesture commit/cancel
 * animations honour this by snapping the row instead of sliding it.
 */
export function createPrefersReducedMotion(): Accessor<boolean> {
  // oxlint-disable-next-line lingui/no-unlocalized-strings -- CSS media query, not UI copy
  return createMediaQuery("(prefers-reduced-motion: reduce)");
}
